import dayjs from "dayjs";
import type { MRT_ColumnFiltersState } from "material-react-table";
import { euroToEurocent } from "@/helper/formattingValues";
import {
  buildDateRangeApiParams,
  isAnyDateRangeFilterValue,
  isDateRangeFilterEnabled,
  isDateRangeValueEmpty,
  normalizeDateRangeValue,
  resolveDateFilterFieldPath,
  resolveFilterDateRangeFormat,
} from "@/components/dateTime/genericDateFilterUtils";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import {
  getDurationFilterApiParams,
  getDurationFilterApiValue,
  isDurationFilterRangeValue,
} from "@/components/tables/genericCrudTableDurationFilterUtils";

export interface CsvExportFilter {
  id: string;
  value: any; // raw MRT filter value or converted api value; CsvExportButton puts it into the url as is
  customFilterParameter: string;
}

// header-bar date range filter, currently only maintained for trackedService in the tableConfig slice
export interface CsvExportHeaderDateFilter {
  filter?: string;
  dateRange?: [unknown, unknown];
}

//   intentionally not the same mapping as buildColumnFilterApiParams.
// - price is always converted to eurocent
// - exact (non-range) date filters and the entity specials (trackedService checked/billed, appointmentRequest include_completed, anchorData row filters, percent) are not mapped
export const buildCsvExportColumnFilters = (
  columnFilters: MRT_ColumnFiltersState,
  columns: CrudColumnDef[],
  allowsDateRangeFilters = true
): CsvExportFilter[] =>
  columnFilters.flatMap(({ id, value }) => {
    const columnDef = columns.find((col) => col.id === id);

    if (
      columnDef?.filterVariant === "date" &&
      isDateRangeFilterEnabled(columnDef, allowsDateRangeFilters) &&
      isAnyDateRangeFilterValue(value)
    ) {
      const range = normalizeDateRangeValue(value);
      if (isDateRangeValueEmpty(range)) {
        return [];
      }
      const fieldPath = resolveDateFilterFieldPath(columnDef, id);
      return Object.entries(
        buildDateRangeApiParams(
          fieldPath,
          range,
          resolveFilterDateRangeFormat(columnDef),
          columnDef.dateFilterApiParams
        )
      ).map(([paramKey, paramValue]) => ({
        id: paramKey,
        value: paramValue,
        customFilterParameter: "",
      }));
    }

    const key = columnDef?.customColumnFilterId || id;
    const customParam =
      columnDef?.customFilterParameter ??
      (columnDef?.setNoFilterParameter === true ? "" : "__icontains");

    let finalValue: unknown = value;
    if (key === "price" && typeof value !== "undefined" && value !== null) {
      finalValue = euroToEurocent((value as any).toString().replace(",", "."));
    }
    if (columnDef?.filterVariant === "duration") {
      if (isDurationFilterRangeValue(value)) {
        return Object.entries(
          getDurationFilterApiParams(key, value, {
            apiUnit: columnDef.durationFilterApiUnit,
            apiValueTransformer: columnDef.durationFilterApiValue,
            rangeParameterSuffixes:
              columnDef.durationFilterRangeParameterSuffixes,
          })
        ).map(([paramKey, paramValue]) => ({
          id: paramKey,
          value: paramValue,
          customFilterParameter: "",
        }));
      }

      finalValue = getDurationFilterApiValue(value, {
        apiUnit: columnDef.durationFilterApiUnit,
        apiValueTransformer: columnDef.durationFilterApiValue,
      });
      if (finalValue === undefined) {
        return [];
      }
    }

    return [
      {
        id: key,
        value: finalValue,
        customFilterParameter: customParam,
      },
    ];
  });

export const buildCsvExportHeaderDateFilters = (
  headerDateFilter: CsvExportHeaderDateFilter | undefined
): CsvExportFilter[] => {
  if (!headerDateFilter?.filter) {
    return [];
  }
  const { filter, dateRange } = headerDateFilter;
  const [start, end] = dateRange as [unknown, unknown];

  const additionalFilters: CsvExportFilter[] = [];
  if (start) {
    additionalFilters.push({
      id: `${filter}__gt`,
      value: encodeURIComponent(
        dayjs(start as dayjs.ConfigType)
          .startOf("day")
          .format()
      ),
      customFilterParameter: "",
    });
  }
  if (end) {
    additionalFilters.push({
      id: `${filter}__lt`,
      value: encodeURIComponent(
        dayjs(end as dayjs.ConfigType)
          .endOf("day")
          .format()
      ),
      customFilterParameter: "",
    });
  }
  return additionalFilters;
};

export const buildCsvExportFilters = (
  columnFilters: MRT_ColumnFiltersState,
  columns: CrudColumnDef[],
  allowsDateRangeFilters: boolean,
  headerDateFilter: CsvExportHeaderDateFilter | undefined
): CsvExportFilter[] => [
  ...buildCsvExportColumnFilters(columnFilters, columns, allowsDateRangeFilters),
  ...buildCsvExportHeaderDateFilters(headerDateFilter),
];

const isMrtDisplayColumnId = (columnId: string) => columnId.startsWith("mrt-");

export const buildCsvExportColumnOrdering = (
  visibleLeafColumnIds: string[],
  columnVisibility: Record<string, boolean>
): string[] =>
  visibleLeafColumnIds
    .filter((columnId) => !isMrtDisplayColumnId(columnId))
    .filter((columnId) => columnVisibility[columnId] !== false);
